import { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';
import type { AkteFristTyp, AkteFristInsert } from '@/lib/database.types';

interface FristSetzenModalProps {
  open: boolean;
  onClose: () => void;
  akteId: string;
  aktenNummer?: string | null;
}

const FRIST_TYPEN: { value: AkteFristTyp; label: string }[] = [
  { value: 'wiedervorlage',             label: 'Wiedervorlage' },
  { value: 'eskalation_mahnstufe',      label: 'Eskalation Mahnstufe' },
  { value: 'verjaehrung_3jahre',        label: 'Verjährung (3 Jahre)' },
  { value: 'gerichtstermin',            label: 'Gerichtstermin' },
  { value: 'eingang_sachstandsbericht', label: 'Eingang Sachstandsbericht' },
  { value: 'rueckruf',                  label: 'Rückruf' },
  { value: 'sonstiges',                 label: 'Sonstiges' },
];

export default function FristSetzenModal({ open, onClose, akteId, aktenNummer }: FristSetzenModalProps) {
  const [typ, setTyp] = useState<AkteFristTyp | ''>('');
  const [faelligAm, setFaelligAm] = useState('');
  const [bemerkung, setBemerkung] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTyp('');
      setFaelligAm('');
      setBemerkung('');
    }
  }, [open]);

  const handleSave = async () => {
    if (!typ) {
      toast.error('Bitte einen Frist-Typ auswählen');
      return;
    }
    if (!faelligAm) {
      toast.error('Bitte ein Fälligkeitsdatum eingeben');
      return;
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const payload: AkteFristInsert = {
        akte_id:      akteId,
        typ:          typ as AkteFristTyp,
        faellig_am:   faelligAm,
        bemerkung:    bemerkung.trim() || null,
        erinnert_am:  null,
        erledigt_von: null,
        created_by:   user?.id ?? null,
      };

      const { error } = await supabase.from('akten_fristen').insert(payload);
      if (error) throw error;

      toast.success('Frist gesetzt', {
        description: `${FRIST_TYPEN.find(t => t.value === typ)?.label} — fällig am ${new Date(faelligAm).toLocaleDateString('de-DE')}`,
      });
      onClose();
    } catch (err: any) {
      toast.error('Fehler beim Speichern: ' + (err.message || 'Unbekannter Fehler'));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:bg-white focus:border-[#1B3A5C]/40 focus:outline-none transition-colors';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base font-bold">
            <Calendar className="w-4 h-4 text-[#1B3A5C]" />
            Frist setzen{aktenNummer ? ` — ${aktenNummer}` : ''}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Frist-Typ */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
              Frist-Typ <span className="text-red-500">*</span>
            </label>
            <select
              value={typ}
              onChange={(e) => setTyp(e.target.value as AkteFristTyp)}
              className={inputCls}
            >
              <option value="">— Bitte wählen —</option>
              {FRIST_TYPEN.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Fällig am */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
              Fällig am <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={faelligAm}
              onChange={(e) => setFaelligAm(e.target.value)}
              className={inputCls}
            />
          </div>

          {/* Bemerkung */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
              Bemerkung <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={bemerkung}
              onChange={(e) => setBemerkung(e.target.value)}
              placeholder="Interne Notiz zur Frist..."
              rows={3}
              className={`${inputCls} resize-none`}
            />
          </div>
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm font-medium text-gray-500 hover:bg-gray-100 transition-colors"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 rounded-xl text-sm font-semibold text-white bg-[#1B3A5C] hover:bg-[#152d49] transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving && (
              <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            )}
            Speichern
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
